<template>
  <div class="csv-import">
    <b-tabs card v-if="success === 0 && errors.length === 0">
      <b-tab :title="$gettext('Single Company')" active>
        <form @submit.stop.prevent="importCsv" v-if="success === 0 && errors.length === 0">
          <b-row>
            <div class="form-group col-12">
              <label for="reference" v-translate>Company</label>
              <company-autocomplete ref="companyAc" @selected="onSearchCompanySelected" v-if="!company" :includes="['country']" required />
              <p v-if="company">
                {{ company.name }} <b-badge variant="danger" href="#" @click.prevent="company = null">×</b-badge>
              </p>
            </div>
          </b-row>
          <b-row v-if="company">
            <b-col cols="12">
              <uploader ref="uploader" accept="text/csv" :endpoint="this.$appConfig.api.baseUrl + 'companies/' + company.id + '/files'" required @success="csvUploaded" />
            </b-col>
            <b-col cols="12">
              <mapbox-static-map v-if="defaultShippingFields.lat" :format="format" :markers="markers" :zoom="-1" :center="center" :size="size" :type="type" :language="language" class="card-img-top d-noneX d-lg-blockX" :customStyle="this.$appConfig.maps.staticMapStyle" style="margin: 20px 0"></mapbox-static-map>
            </b-col>
            <div class="form-group col-sm-7">
              <label for="from_extra" v-translate>Street and number</label>
              <div class="input-group" v-if="!defaultShippingFields.googlePlaceString">
                <address-autocomplete ref="addressAutocomplete" @selected="selectedItem" :minCharacter="4" :required="true" />
              </div>
              <p v-if="defaultShippingFields.googlePlaceString">
                {{ defaultShippingFields.googlePlaceString }} <b-badge variant="danger" href="#" @click.prevent="defaultShippingFields.googlePlaceString = null; defaultShippingFields.placeId = null; defaultShippingFields.lat = null; defaultShippingFields.lng = null">×</b-badge>
              </p>
            </div>
            <div class="form-group col-sm-4">
              <label for="floor" v-translate>Floor, door, etc.</label>
              <div class="input-group">
                <b-form-input id="floor" v-model="defaultShippingFields.floor" />
              </div>
            </div>
            <div class="form-group col-sm-6">
              <label for="contact-name" v-translate>Contact First Name</label>
              <div class="input-group">
                <b-form-input id="contact-name" v-model="defaultShippingFields.contact.firstName" required />
              </div>
            </div>
            <div class="form-group col-sm-6">
              <label for="contact-lastname" v-translate>Contact Last Name</label>
              <div class="input-group">
                <b-form-input id="contact-lastname" v-model="defaultShippingFields.contact.lastName" />
              </div>
            </div>
            <div class="form-group col-sm-6">
              <label for="contact-email" v-translate>Contact Email</label>
              <div class="input-group">
                <b-form-input id="contact-email" type="email" v-model="defaultShippingFields.contact.email" />
              </div>
            </div>
            <div class="form-group col-sm-6">
              <label for="from_phone" v-translate>Contact Phone</label>
              <div class="input-group">
                <cleave :options="{ phone: true, phoneRegionCode: this.$cookies.get('country').toLowerCase() }" class="form-control" v-model="defaultShippingFields.contact.phone" autocomplete="nope"></cleave>
              </div>
            </div>
            <div class="text-right form-group col-12 form-actions">
              <b-button type="submit" variant="primary" size="lg" v-translate>Process file</b-button>
              <b-button type="button" variant="outline-primary" size="lg" @click.prevent="emitCancel" v-translate>Cancel</b-button>
            </div>
          </b-row>
        </form>
      </b-tab>
      <b-tab :title="$gettext('Multiple Companies')">
        <b-alert variant="warning" show>
          <span v-translate>Remember, the CSV must have all the required FROM and TO fields and the <code>company_id</code> column filled with right data in order to work OK.</span>
        </b-alert>
        <form @submit.stop.prevent="importCsvMulti" v-if="success === 0 && errors.length === 0">
          <b-row v-if="isInternational">
            <div class="form-group col-12">
              <label for="reference" v-translate>Companies Country</label>
              <b-form-select v-model="country" :options="countriesSelect" class="mb-3">
                <template slot="first">
                  <option :value="null" v-translate>Select Country</option>
                </template>
              </b-form-select>
            </div>
          </b-row>
          <b-row v-if="currency">
            <b-col cols="12">
              <uploader ref="uploader" accept="text/csv" :endpoint="this.$appConfig.api.baseUrl + 'companies/files'" required @success="csvUploaded" @changed="fileChanged" />
            </b-col>
            <div class="text-right form-group col-12 form-actions">
              <b-button type="submit" variant="primary" size="lg" v-translate>Process file</b-button>
              <b-button type="button" variant="outline-primary" size="lg" @click.prevent="emitCancel" v-translate>Cancel</b-button>
            </div>
          </b-row>
        </form>
      </b-tab>
    </b-tabs>
    <div v-else-if="success > 0">
      <b-row>
        <b-col>
          <h1 v-translate="{qty: success}">%{qty} shippings have been created! 😃</h1>
          <b-col class="text-center form-actions my-5">
            <b-button type="button" variant="secondary" size="lg" @click.prevent="openShippings" v-translate>Open created shippings in new tabs</b-button>
          </b-col>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="text-center form-actions">
          <b-button type="button" variant="outline-primary" size="lg" @click.prevent="emitCancel" v-translate>Close</b-button>
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <b-row>
        <b-col>
          <h1 v-translate>There were errors processing the file</h1>
          <ul>
            <li v-for="(error, index) in errors" :key="index">
              <strong>{{ error.title }}</strong>: {{error.detail}}
            </li>
          </ul>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="text-center form-actions">
          <b-button type="button" variant="primary" size="lg" @click.prevent="resetData" v-translate>Start over</b-button>
          <b-button type="button" variant="outline-primary" size="lg" @click.prevent="emitCancel" v-translate>Close</b-button>
        </b-col>
      </b-row>
    </div>
    <div v-if="processing" style="position:fixed; top: 0; left: 0; width: 100%; height: 100vh; background: rgba(0,0,0,.5); z-index: 10001">
      <div id="spinner" class="loading"><div class="loader"><div class="loader"><div class="loader"><div class="loader"></div></div></div></div></div>
    </div>
  </div>
</template>
<script>
import AddressMixins from '@/mixins/address.mixin.js'
import AddressAutocomplete from '@/components/autocompletes/AddressAutocomplete'
import Cleave from 'vue-cleave'
import MapboxStaticMap from '@/components/MapboxStaticMap'
import Uploader from '@/components/Uploader'
import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
import * as XLSX from '@e965/xlsx'

export default {
  name: 'CsvImport',
  components: {
    AddressAutocomplete,
    Cleave,
    MapboxStaticMap,
    Uploader,
    CompanyAutocomplete
  },
  data() {
    return {
      searchCompanyQuery: '',
      companyAcOptions: {
        debounce: 500,
        placeholder: this.$gettext('Company Name, Business Name or TIN'),
        inputClass: 'form-control'
      },
      minCharacter: 2,
      company: null,
      defaultShippingFields: {
        placeId: null,
        googlePlaceString: null,
        lat: null,
        lng: null,
        street: '',
        number: '',
        floor: '',
        apartment: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'ARG',
        instructions: '',
        contact: {
          firstName: '',
          lastName: '',
          email: '',
          phone: ''
        },
        message: ''
      },
      type: 'roadmap',
      size: [600, 300],
      config: this.$appConfig,
      apiKey: this.$appConfig.maps.api_key, // required
      zoom: 15, // required
      center: '',
      format: 'png',
      language: 'es',
      mapStyle: this.$appConfig.maps.staticMapStyle,
      markers: [],
      file: null,
      payload: {},
      processing: false,
      success: 0,
      shippings: [],
      errors: [],
      country: null,
      currency: null,
      toPostProcess: []
    }
  },
  props: {
    isProfile: {
      type: Boolean,
      default: false,
      required: false
    },
    user: {
      type: Object,
      default: null
    }
  },
  watch: {
    country(val) {
      if (val !== null) {
        this.currency = window.countries.find(x => x.iso_code === val).currency
      } else {
        this.currency = null
      }
    }
  },
  mixins: [AddressMixins],
  created() {
    this.fetchCountries()
  },
  mounted() {
    this.country = window.country || null
  },
  methods: {
    importCsv() {
      if (!this.file) {
        this.$refs.uploader.warning = true
      } else {
        this.payload = {
          file: this.file,
          currency: this.company.currency,
          type: 'regular',
          from: this.defaultShippingFields,
          company_id: this.company.id
        }

        this.processing = true
        // save default bulk shipping from for comapny
        this.$http.patch(this.$appConfig.api.baseUrl + 'companies/' + this.company.id, {default_shipping_fields: this.defaultShippingFields})
          .then(response => {
            this.$http.post(this.$appConfig.api.baseUrl + 'companies/' + this.company.id + '/shippings/bulk', this.payload)
              .then(response => {
                this.shippings = response.body.shippings
                this.success = this.shippings.length
                this.emitSuccess()
                this.processing = false
              }, () => {
                this.$emit('cancel')
                this.processing = false
              })
          })
      }
    },
    async importCsvMulti() {
      if (!this.file) {
        this.$refs.uploader.warning = true
      } else {
        this.payload = {
          file: this.file,
          currency: this.currency,
          type: 'regular'
        }
        this.processing = true
        await this.$http.post(this.$appConfig.api.baseUrl + 'shippings/bulk', this.payload)
          .then(async response => {
            this.shippings = response.body.shippings
            this.shippings.forEach(async (shipping, i) => {
              if (this.toPostProcess[i]) {
                await this.tryPostProcess(shipping, this.toPostProcess[i])
              }
            })
            this.success = this.shippings.length
            this.emitSuccess()
            this.processing = false
          }, () => {
            this.$emit('cancel')
            this.processing = false
          })
      }
    },
    async tryPostProcess(id, data) {
      let payload = {}
      if (data.price) {
        payload.price = data.price
      }
      if (data.courier_commission) {
        payload.courier_commission = data.courier_commission
      }
      if (payload) {
        await this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + id, payload)
      }
      if (data.courier_id) {
        await this.$http.post(this.$appConfig.api.baseUrl + 'status', {shipping_id: id, user_id: data.courier_id, status: 'CONFIRMED'})
      }
    },
    csvUploaded(obj) {
      this.file = obj.full_path
    },
    fileChanged(file) {
      file = file[0]
      const reader = new FileReader()
      reader.onload = e => {
        let workbook
        try {
          console.log('Try CSV')
          workbook = XLSX.read(new TextDecoder('utf-8').decode(e.target.result), {
            type: 'string',
            raw: true,
            ...this.options
          })
        } catch (error) {
          console.log('Go XLSX')
          workbook = XLSX.read(e.target.result, {
            type: 'buffer',
            raw: true,
            ...this.options
          })
        }
        workbook.SheetNames.forEach(async (sheetName, sheetIndex) => {
          if (sheetIndex === 0) {
            let XLRowObject = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName])
            let row = 1
            let toPostProcess = XLRowObject.map((x, i) => {
              row++
              let moovaObject = {
                line: row,
                courier_id: x['courier_id'],
                courier_commission: x['courier_commission'],
                price: x['price']
              }
              return x['courier_id'] || x['courier_commission'] || x['price'] ? moovaObject : null
            })
            this.toPostProcess = toPostProcess
          }
        })
      }
      reader.onerror = e => {
        console.log('ERROR', e)
      }
      reader.readAsArrayBuffer(file)
    },
    autocompleteEndpoint(query) {
      if (query.length >= 3) {
        return this.$appConfig.api.baseUrl + 'autocomplete?query=' + query
      } else {
        return
      }
    },
    async selectedItem(item) {
      await this.checkPlace(item)
      this.$nextTick(() => {
        if (this.theCheckedAddress.id) {
          this.defaultShippingFields.placeId = this.theCheckedAddress.id
          this.defaultShippingFields.googlePlaceString = this.theCheckedAddress.formatted_address
          this.defaultShippingFields.lat = this.theCheckedAddress.lat
          this.defaultShippingFields.lng = this.theCheckedAddress.lng
          this.markers = []
          this.markers.push({
            id: 10000,
            lat: this.defaultShippingFields.lat,
            lng: this.defaultShippingFields.lng,
            icon: this.$appConfig.staticBaseUrl + 'static/img/ic_origin_on.svg',
            x: 10,
            y: 10,
            infoText: this.$gettext('Origin'),
            tooltip: true
          })
        }
        this.theCheckedAddress = {}
      })
      this.defaultShippingFields.googlePlaceString = this.formattedDisplay(item)
    },
    formattedDisplay(result) {
      return result.main_text + ', ' + result.secondary_text
    },
    emitCancel() {
      this.$emit('cancel')
    },
    emitSuccess() {
      this.$emit('success')
    },
    onSearchCompanySelected(item) {
      this.company = {id: item.id, name: item.name, defaultShippingFields: item.default_shipping_fields, currency: item.country.currency}

      if (!this.company.defaultShippingFields) {
        this.defaultShippingFields.contact.firstName = item.contact_name
        this.defaultShippingFields.contact.email = item.email
        this.defaultShippingFields.contact.phone = item.phone
      } else {
        this.defaultShippingFields = item.default_shipping_fields
        if (item.default_shipping_fields.lat) {
          this.markers = []
          this.markers.push({
            id: '0',
            lat: item.default_shipping_fields.lat,
            lng: item.default_shipping_fields.lng,
            icon: this.$appConfig.staticBaseUrl + 'static/img/ico-from_green.png',
            x: 10,
            y: 10,
            infoText: this.$gettext('Origin')
          })
        }
      }
      this.searchCompanyQuery = ''
    },
    openShippings() {
      for (const shipping of this.shippings) {
        window.open('/edit/shippings/' + shipping, shipping)
      }
    },
    resetData: function () {
      Object.assign(this.$data, this.$options.data.call(this))
    }
  }
}

</script>
